import { getNodesBounds, getViewportForBounds, type Node } from '@xyflow/react';
import { toJpeg, toSvg } from 'html-to-image';

/**
 * Capture canvas matches the drawing field above the title block on the A3 sheet
 * so object-fit:contain fills almost the whole frame (no baked-in empty margins).
 */
export const DRAWING_FIELD_W = 2400;
export const DRAWING_FIELD_H = 1520;

const MIN_ZOOM = 0.02;
const MAX_ZOOM = 8;

/** Tight clearance for edge badges / orthogonal stubs — avoid huge empty margins. */
export function expandBoundsForEdges(bounds: {
  x: number;
  y: number;
  width: number;
  height: number;
}) {
  const pad = Math.max(48, Math.min(88, Math.ceil(Math.max(bounds.width, bounds.height) * 0.035)));
  return {
    x: bounds.x - pad,
    y: bounds.y - pad,
    width: Math.max(bounds.width + pad * 2, 160),
    height: Math.max(bounds.height + pad * 2, 120),
  };
}

/** Output size for the topology JPEG (= drawing field on the sheet). */
export function sizeForBounds(_bounds: { width: number; height: number }) {
  return { width: DRAWING_FIELD_W, height: DRAWING_FIELD_H };
}

async function withLightThemeForCapture<T>(fn: () => Promise<T>): Promise<T> {
  const root = document.documentElement;
  const hadDark = root.classList.contains('dark');
  const prevScheme = root.style.colorScheme;
  root.classList.remove('dark');
  root.classList.add('ukrop-export-capture');
  root.style.colorScheme = 'light';
  try {
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        window.setTimeout(() => resolve(), 80);
      });
    });
    return await fn();
  } finally {
    root.classList.remove('ukrop-export-capture');
    if (hadDark) root.classList.add('dark');
    root.style.colorScheme = prevScheme || (hadDark ? 'dark' : 'light');
  }
}

function filterCaptureNode(domNode: HTMLElement): boolean {
  const cls = domNode.getAttribute?.('class') ?? '';
  if (cls.includes('react-flow__attribution')) return false;
  if (cls.includes('react-flow__controls')) return false;
  if (cls.includes('react-flow__minimap')) return false;
  if (cls.includes('react-flow__panel')) return false;
  if (cls.includes('react-flow__selection')) return false;
  if (cls.includes('react-flow__nodesselection')) return false;
  if (cls.includes('react-flow__resize-control')) return false;
  if (cls.includes('align-guides')) return false;
  return true;
}

/**
 * Capture full topology in light theme for print.
 * Content is scaled to fill the drawing-field rectangle (tight padding).
 */
export async function captureDiagramJpeg(nodes: Node[]): Promise<string | null> {
  return captureViewport(nodes, 'jpeg');
}

export async function captureDiagramSvg(nodes: Node[]): Promise<string | null> {
  return captureViewport(nodes, 'svg');
}

async function captureViewport(nodes: Node[], kind: 'jpeg' | 'svg'): Promise<string | null> {
  if (!nodes.length) return null;

  const viewportEl = document.querySelector('.react-flow__viewport') as HTMLElement | null;
  if (!viewportEl) return null;

  const sized = nodes.map((n) => ({
    ...n,
    width: n.measured?.width ?? n.width ?? (n.style?.width as number | undefined) ?? 160,
    height: n.measured?.height ?? n.height ?? (n.style?.height as number | undefined) ?? 90,
  }));
  const rawBounds = getNodesBounds(sized);
  const bounds = expandBoundsForEdges(rawBounds);
  const { width: imageW, height: imageH } = sizeForBounds(bounds);
  const viewport = getViewportForBounds(bounds, imageW, imageH, MIN_ZOOM, MAX_ZOOM, '2%');
  const common = {
    backgroundColor: '#ffffff',
    cacheBust: true,
    pixelRatio: 2,
    width: imageW,
    height: imageH,
    filter: filterCaptureNode,
    style: {
      width: `${imageW}px`,
      height: `${imageH}px`,
      background: '#ffffff',
      transformOrigin: '0 0',
      transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
    },
  };

  return withLightThemeForCapture(() =>
    kind === 'jpeg'
      ? toJpeg(viewportEl, { ...common, quality: 0.95 })
      : toSvg(viewportEl, common),
  );
}

export function downloadDataUrl(dataUrl: string, filename: string) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  a.click();
}
